import { useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Loader2, Camera, X } from "lucide-react";
import { normalizeScan } from "@/lib/scan";
import { resolveScannedTag, tagStatusWords } from "@/lib/tagScan";
import { ScanField } from "@/components/ScanField";
import { scanAccept, scanRefuse } from "@/lib/scanFeedback";

// A PLAIN-LOOKING BOX THAT IS SAFE TO SCAN INTO — AND NOW TAKES THE CAMERA TOO.
//
// A scan gun is a keyboard: it TYPES whatever the code holds into whichever box has focus.
// So any ordinary <Input> on a screen a scanner is used on will happily accept
// "https://…/scan/part/<token>". This is that <Input> with the one shared extractor wired
// into its change handler, plus — when `resolveTag` is set — the one shared tag lookup on
// blur/Enter, so the box settles on the FAS number printed on the physical tag.
//
// WHY THE CAMERA IS HERE NOW (his report, 2026-10-13, THE THIRD TIME): *"I was creating a CHILD
// part out in the yard on my iPhone, and there was no option to scan the tag with the camera. I
// had to type it in. That's the third screen I've hit this on."* He was right, and the cause was
// this file: `ScanField` has camera + gun + typing and is used by 45 boxes, while THIS box had
// gun + typing only — and all three screens he hit are its three uses (the child-part tag rows,
// Link parent, and capturing a part off a truck). A phone has no scan gun, so a box without a
// camera means typing a ten-character tag with gloves on, in the yard.
//
// ONE SCANNER, NOT TWO: the camera here IS `ScanField` — its single-camera lock, torch, focus
// control, frame-timeout handling and iOS quirks, not a second implementation that would drift.
// `camera` defaults to TRUE on his ruling: *"Q16 CAMERA ON BY DEFAULT in every box: yes."*
//
// THE SWEEP, 2026-10-13 (his instruction: *"CAMERA SCAN EVERYWHERE — every box, same scanner,
// camera on by default, with the gate test"*): this box is now used by every screen box that takes
// a code off something physical — a VIN, a FAS tag, a part or serial number, a bin, shelf, sector
// or lot code — and `backend/tests/test_every_box_that_takes_a_code_can_be_scanned.py` fails the
// build if a new one appears without it.
export function ScanTextInput({ value, onChange, resolveTag = false, onResolved = null,
                               testid, className = "", inputClassName = "", camera = true,
                               inputRef = null, onEnter = null, ...rest }) {
  const [busy, setBusy] = useState(false);
  const [camOpen, setCamOpen] = useState(false);
  const lastResolved = useRef("");

  // TYPING IS NOT A SCAN. `normalizeScan` trims and unwraps a scanned QR URL — right for a scan,
  // wrong on every keystroke: it swallowed the space in a part number typed as `4534 001 996`.
  // A scan is rewritten (AIM prefix, QR URL); ordinary typing is passed through untouched.
  const change = (raw) => {
    const v = normalizeScan(raw);
    onChange(v === (raw || "").trim() ? raw : v);
  };

  // `override` exists because a camera scan has to settle the value it just read: React props
  // have not updated yet in that tick, so reading `value` here would settle the OLD text.
  const settle = async (override = null) => {
    const v = (override ?? value ?? "").trim();
    if (!resolveTag || !v || v === lastResolved.current) return;
    setBusy(true);
    try {
      const r = await resolveScannedTag(v);
      lastResolved.current = r.code || v;
      if (r.code) onChange(r.code);
      // THE ONE SCAN ANSWER: a box that looks a tag up answers it here, so every screen using
      // this box behaves the same without copying the cue (his standard, 2026-10-16).
      if (!r.code) scanRefuse(r.error || "COULDN'T READ THAT TAG");
      else if (r.boundPart || r.status === "voided" || r.status === "retired") scanRefuse(tagStatusWords(r));
      else scanAccept(r.code);
      onResolved?.(r);
    } finally { setBusy(false); }
  };

  // One tag, one box: the camera closes itself the moment it reads one, so a row never sits
  // there holding the lens open after it has what it needs. (Measured in the iPhone run: open
  // to filled in under a second.)
  const scanned = async (code) => {
    const v = normalizeScan(code);
    if (!v) { scanRefuse("NOTHING READ — TRY AGAIN"); return; }
    onChange(v);
    setCamOpen(false);
    // When the box looks the tag up, `settle` gives the answer (it knows whether the tag is free).
    // When it does not, the read itself IS the answer.
    if (resolveTag) await settle(v);
    else scanAccept(v);
  };

  return (
    <div className={className}>
      <div className="flex items-stretch gap-1.5">
        <div className="relative flex-1">
          <Input data-testid={testid} value={value || ""} ref={inputRef}
            onChange={(e) => change(e.target.value)}
            onPaste={(e) => { e.preventDefault(); change(e.clipboardData.getData("text")); }}
            onBlur={() => settle()}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); settle(); onEnter?.(); } }}
            className={`rounded-lg border-slate-200 font-mono w-full pr-8 ${inputClassName}`} {...rest} />
          {busy && <Loader2 data-testid={`${testid}-resolving`} className="w-4 h-4 animate-spin text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2" />}
        </div>
        {camera && (
          // A GLOVE-SIZED TARGET, not an icon tucked inside the box: 44px wide, full height.
          <button type="button" data-testid={`${testid}-camera`} onClick={() => setCamOpen((o) => !o)}
            title={camOpen ? "Close the camera" : "Scan the tag with the camera"}
            aria-label={camOpen ? "Close the camera" : "Scan the tag with the camera"}
            className={`shrink-0 w-11 rounded-lg border flex items-center justify-center transition-colors ${
              camOpen ? "border-[#B01E28] bg-[#B01E28] text-white"
                      : "border-slate-200 bg-white text-slate-600 hover:border-[#E0781A] hover:text-[#E0781A]"}`}>
            {camOpen ? <X className="w-4 h-4" /> : <Camera className="w-4 h-4" />}
          </button>
        )}
      </div>
      {camera && camOpen && (
        <div data-testid={`${testid}-camera-panel`} className="mt-2 rounded-lg border border-slate-200 bg-slate-50 p-2">
          <ScanField testid={`${testid}-cam`} onScan={scanned} autoStartCamera autoFocus={false}
            uppercase placeholder="…or type the tag here" submitLabel="Use this" />
        </div>
      )}
    </div>
  );
}
